import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaTrash, FaSearch, FaEnvelope, FaReply, FaArchive } from 'react-icons/fa';
import { useToast } from '../../context/ToastContext';
import { LoadingState } from '../../components/Loading';
import Modal from '../../components/Modal';

const STATUS_BADGE = {
  new: 'info',
  read: 'neutral',
  replied: 'success',
  archived: 'warning',
};

const AdminMessages = () => {
  const toast = useToast();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [viewing, setViewing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => { loadMessages(); }, []);

  const loadMessages = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/contact');
      setMessages(res.data);
    } catch (e) {
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const openMessage = async (msg) => {
    setViewing(msg);
    if (msg.status === 'new') {
      try {
        await axios.put(`/api/contact/${msg._id}`, { status: 'read' });
        setMessages(messages.map((m) => m._id === msg._id ? { ...m, status: 'read' } : m));
      } catch (e) { /* silent */ }
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`/api/contact/${id}`, { status });
      toast.success('Status updated');
      setMessages(messages.map((m) => m._id === id ? { ...m, status } : m));
      if (viewing?._id === id) setViewing({ ...viewing, status });
    } catch (e) {
      toast.error('Update failed');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`/api/contact/${deleteTarget._id}`);
      toast.success('Message deleted');
      setMessages(messages.filter((m) => m._id !== deleteTarget._id));
      setDeleteTarget(null);
      if (viewing?._id === deleteTarget._id) setViewing(null);
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  const filtered = messages.filter((m) => {
    const matchesSearch = !search ||
      m.name.toLowerCase().includes(search.toLowerCase()) ||
      m.email.toLowerCase().includes(search.toLowerCase()) ||
      (m.subject || '').toLowerCase().includes(search.toLowerCase()) ||
      m.message.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = !statusFilter || m.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const newCount = messages.filter((m) => m.status === 'new').length;

  return (
    <div>
      <div className="admin-header">
        <div>
          <h1>Messages</h1>
          <p className="admin-header-subtitle">
            {messages.length} total · {newCount} unread
          </p>
        </div>
      </div>

      <div className="admin-toolbar">
        <div className="search-wrapper">
          <FaSearch />
          <input
            type="search"
            placeholder="Search messages..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">All statuses</option>
          <option value="new">New</option>
          <option value="read">Read</option>
          <option value="replied">Replied</option>
          <option value="archived">Archived</option>
        </select>
      </div>

      {loading ? (
        <LoadingState />
      ) : filtered.length === 0 ? (
        <div className="card empty-state">
          <FaEnvelope className="empty-state-icon" />
          <h3>No messages found</h3>
          <p>{search || statusFilter ? 'Try clearing your filters' : 'Customer messages will appear here'}</p>
        </div>
      ) : (
        <div className="admin-table">
          <div className="admin-table-scroll">
            <table>
              <thead>
                <tr>
                  <th>From</th>
                  <th>Subject</th>
                  <th>Preview</th>
                  <th>Status</th>
                  <th>Received</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((msg) => (
                  <tr
                    key={msg._id}
                    onClick={() => openMessage(msg)}
                    style={{
                      cursor: 'pointer',
                      fontWeight: msg.status === 'new' ? 600 : 400,
                    }}
                  >
                    <td>
                      <div style={{ fontWeight: 600 }}>{msg.name}</div>
                      <small style={{ color: 'var(--gray-500)' }}>{msg.email}</small>
                    </td>
                    <td style={{ color: 'var(--gray-700)' }}>{msg.subject || '—'}</td>
                    <td style={{
                      maxWidth: 280,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      color: 'var(--gray-600)',
                      fontSize: 13,
                    }}>
                      {msg.message}
                    </td>
                    <td>
                      <span className={`badge ${STATUS_BADGE[msg.status] || 'neutral'}`}>
                        {msg.status}
                      </span>
                    </td>
                    <td style={{ fontSize: 13, color: 'var(--gray-500)', whiteSpace: 'nowrap' }}>
                      {new Date(msg.createdAt).toLocaleDateString()}
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div className="action-buttons">
                        <button
                          className="btn-icon btn-primary"
                          onClick={() => openMessage(msg)}
                          title="View"
                        >
                          <FaEnvelope />
                        </button>
                        <button
                          className="btn-icon btn-danger"
                          onClick={() => setDeleteTarget(msg)}
                          title="Delete"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* View Modal */}
      <Modal
        isOpen={!!viewing}
        onClose={() => setViewing(null)}
        title={viewing?.subject || 'Message'}
        size="lg"
        footer={
          <>
            {viewing?.status !== 'replied' && (
              <button className="btn btn-success" onClick={() => updateStatus(viewing._id, 'replied')}>
                <FaReply /> Mark as replied
              </button>
            )}
            {viewing?.status !== 'archived' && (
              <button className="btn btn-secondary" onClick={() => updateStatus(viewing._id, 'archived')}>
                <FaArchive /> Archive
              </button>
            )}
            <button className="btn btn-primary" onClick={() => setViewing(null)}>Close</button>
          </>
        }
      >
        {viewing && (
          <>
            <div style={{
              display: 'flex',
              gap: 16,
              padding: '16px',
              background: 'var(--gray-50)',
              borderRadius: 'var(--radius)',
              marginBottom: 20,
              flexWrap: 'wrap',
            }}>
              <div>
                <div style={{ fontSize: 12, color: 'var(--gray-500)', marginBottom: 2 }}>From</div>
                <div style={{ fontWeight: 600 }}>{viewing.name}</div>
                <a href={`mailto:${viewing.email}`} style={{ fontSize: 13, color: 'var(--primary-700)' }}>
                  {viewing.email}
                </a>
              </div>
              {viewing.phone && (
                <div>
                  <div style={{ fontSize: 12, color: 'var(--gray-500)', marginBottom: 2 }}>Phone</div>
                  <a href={`tel:${viewing.phone}`} style={{ fontSize: 14, color: 'var(--primary-700)' }}>
                    {viewing.phone}
                  </a>
                </div>
              )}
              <div style={{ marginLeft: 'auto' }}>
                <div style={{ fontSize: 12, color: 'var(--gray-500)', marginBottom: 2 }}>Received</div>
                <div style={{ fontSize: 13 }}>
                  {new Date(viewing.createdAt).toLocaleString()}
                </div>
              </div>
            </div>

            <div style={{
              padding: 20,
              background: 'white',
              border: '1px solid var(--gray-100)',
              borderRadius: 'var(--radius)',
              whiteSpace: 'pre-wrap',
              lineHeight: 1.7,
              color: 'var(--gray-800)',
              fontSize: 15,
            }}>
              {viewing.message}
            </div>

            <div style={{ marginTop: 20, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <a
                href={`mailto:${viewing.email}?subject=Re: ${viewing.subject || 'Your inquiry'}`}
                className="btn btn-primary"
              >
                <FaReply /> Reply by email
              </a>
              {viewing.phone && (
                <a href={`tel:${viewing.phone}`} className="btn btn-secondary">
                  Call {viewing.phone}
                </a>
              )}
            </div>
          </>
        )}
      </Modal>

      <Modal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Delete message?"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setDeleteTarget(null)}>Cancel</button>
            <button className="btn btn-danger" onClick={handleDelete}>Delete</button>
          </>
        }
      >
        <p>Delete the message from <strong>{deleteTarget?.name}</strong>? This action cannot be undone.</p>
      </Modal>
    </div>
  );
};

export default AdminMessages;
